<template>
  <div>

    <div class="text-danger p-3" v-if="isExpiredToday">
      <i class="fa fa-exclamation-triangle"></i>
      The selected timesheet rate expires today. You cannot enter a time later than today's date.
    </div>

    <div class="text-danger p-3" v-if="!rates.length">
      <i class="fa fa-exclamation-triangle"></i>
      There are no active timesheet rates, please contact the administrator.
    </div>

    <div class="text-danger pt-0 pb-3 px-3" v-if="!activeRate.is_enabled">
      <i class="fa fa-exclamation-triangle"></i>
      The current timesheet rate is inactive. Please select another one to save or submit the timesheet for review.
    </div>

    <div class="row justify-content-center">

      <div class="col-lg-4" v-if="allRates.length <= 1">
        <div class="widget navy-bg py-1 px-3">
          <div class="row vertical-align">
            <div class="col-6 pt-1">
              <span>Rate name:</span>
            </div>
            <div class="col-6 text-right">
              <h5 class="font-bold">{{ activeRate.name }}</h5>
            </div>
          </div>
        </div>
      </div>

      <div class="col-lg-3">
        <div class="widget lazur-bg py-1 px-3">
          <div class="row vertical-align">
            <div class="col-6 pt-1">
              <span>Rate type:</span>
            </div>
            <div class="col-6 text-right">
              <h5 class="font-bold">{{ rateTypeFormatted }}</h5>
            </div>
          </div>
        </div>
      </div>

      <div class="col-lg-3">
        <div class="widget yellow-bg py-1 px-3">
          <div class="row vertical-align">
            <div class="col-6 pt-1">
              <span>Pay rate:</span>
            </div>
            <div class="col-6 text-right">
              <h5 class="font-bold">{{ payRateFormatted }}</h5>
            </div>
          </div>
        </div>
      </div>

    </div>

    <div class="hr-line-dashed"></div>

    <div class="row mt-2">

      <div class="col-md-6 col-sm-12">

        <div class="form-group row">
          <label class="col-sm-4 col-form-label">Date:</label>

          <div class="col-sm-8">
            <div class="date">
              <datepicker
                v-model="date"
                name="date"
                placeholder="Select a date"
                required>
              </datepicker>
            </div>
          </div>
        </div>

      </div>

      <div class="col-md-6 col-sm-12">

        <div class="form-group row" v-if="allRates.length > 1">
          <label class="col-sm-4 col-form-label">Timesheet Rate:</label>
          <div class="col-sm-8">
            <chosen name="timesheet_rate_id" v-model="rateId"
                    :options="ratesList"
                    placeholder="Select Timesheet Rate" class="form-control form-control-sm"></chosen>
          </div>
        </div>
        <input type="hidden" name="timesheet_rate_id" :value="rateId" v-else>

      </div>

    </div>

    <div class="hr-line-dashed"></div>

    <div class="row mt-3 pt-2">
      <div class="col-md-12">

        <div v-if="timeLimitedRateMessage" class="text-danger mb-3 ml-2">
          <i class="fa fa-exclamation-triangle"></i>
          {{ timeLimitedRateMessage }}
        </div>

        <div class="table-responsive">
          <table class="table">
            <thead>
            <tr>
              <th class="border-0"></th>
              <th v-for="date in dates" class="text-center border-0 small font-weight-bold">
                {{ getDayOfWeek(date) }} - {{ date }}
              </th>
            </tr>
            </thead>
            <tbody>
            <tr>
              <td class="font-weight-bold border-0">Time:</td>
              <td v-for="(time, index) in times" class="border-0">

                <div v-if="activeRate.type === 'daily'" class="select-wrapper">
                  <select name="time[]" v-model="times[index]"
                          class="form-control form-control-sm text-right"
                          :style="{background: editableTime[index] ? '#fff' : '#eee'}"
                          required :disabled="partiallyEditable">
                    <option value="0">none</option>
                    <option value="0.25">1/4 day</option>
                    <option value="0.5">1/2 day</option>
                    <option value="0.75">3/4 day</option>
                    <option value="1">1 day</option>
                  </select>
                  <div v-if="!editableTime[index]" class="stub"></div>
                </div>

                <input v-else type="number" name="time[]" v-model="times[index]" min="0" :max="maxTime"
                       step="0.25" class="form-control form-control-sm text-right" required
                       :disabled="partiallyEditable" :readonly="!editableTime[index]">
              </td>
            </tr>
            <tr>
              <td class="font-weight-bold border-0">Subtotal:</td>
              <td v-for="subtotal in subtotalsFormatted" class="text-right font-weight-bold border-0">
                {{ subtotal }}
              </td>
            </tr>
            </tbody>
          </table>
        </div>

        <div class="form-group row float-right mr-2">
          <span class="col-form-label font-weight-bold">Total: {{ totalFormatted }}</span>
        </div>

      </div>
    </div>

    <div class="hr-line-dashed"></div>
  </div>
</template>

<script>

const DATE_FORMAT = 'MM/DD/YYYY';

export default {

  props: {

    rates: {
      type: Array,
      required: true,
    },

    initialTimes: {
      type: Array,
      default: function () {
        return [0, 0, 0, 0, 0, 0, 0];
      }
    },

    initialDate: {
      type: String,
      default: function () {
        return moment().day(1).subtract(1, 'weeks').format(DATE_FORMAT)
      }
    },

    initialRate: {
      type: Object,
      required: false,
    },

    timesheetId: {
      type: Number,
      required: false,
    },

    partiallyEditable: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      times: this.initialTimes,
      date: this.initialDate,
      rateId: this.initialRate?.id || this.rates[this.rates.length - 1].id,
      dateConfig: {
        format: DATE_FORMAT,
        useCurrent: false,
      },
    }
  },

  watch: {
    date: function () {
      this.resetTime();

    },
    rateId: function () {
      this.resetTime();
    },
  },

  computed: {
    ratesList() {
      return Object.assign(...this.allRates.map(item => ({[item.id]: item.name})))
    },

    // rates including an inactive current rate
    allRates() {
      let allRates = this.rates;

      if (!this.rates.some(item => item.id === this.initialRate?.id)) {
        allRates = allRates.concat(this.initialRate);
      }
      return allRates;
    },

    activeRate() {
      return this.allRates.filter(rate => parseInt(rate.id) === parseInt(this.rateId))[0];
    },

    maxTime() {
      return this.activeRate.type === 'daily' ? 1 : 24;
    },

    dates() {
      const currentDate = moment(this.date, DATE_FORMAT);

      // Calculate the date of the Monday of the selected week
      currentDate.startOf('isoWeek');

      let dates = [];
      // Update the list of labels with the dates of the week
      for (let i = 0; i < 7; i++) {
        dates[i] = currentDate.format(DATE_FORMAT);
        currentDate.add(1, 'days');
      }
      return dates;
    },

    subtotals() {
      return this.times.map(time => (this.isNumeric(parseFloat(time)) ? time : 0) * this.activeRate.pay_rate);
    },

    total() {
      return _.sum(this.subtotals)
    },

    subtotalsFormatted() {
      return this.subtotals.map(subtotal => '$' + subtotal.toFixed(2));
    },

    totalFormatted() {
      return '$' + this.total.toFixed(2);
    },

    payRateFormatted() {
      return '$' + this.activeRate.pay_rate;
    },

    rateTypeFormatted() {
      return _.capitalize(this.activeRate.type);
    },

    isExpiredToday() {
      const expiredDate = this.activeRate.expired_at ? moment(this.activeRate.expired_at).startOf('day') : null;

      if (!expiredDate)
        return false;

      return expiredDate.isSame(moment().startOf('day'));
    },

    editableTime() {
      const formatDate = date => date ? moment(date).startOf('day') : null;
      const activatedDate = formatDate(this.activeRate.activated_at);
      const expiredDate = formatDate(this.activeRate.expired_at);

      return this.dates.map(function (date) {
        date = moment(date, 'MM/DD/YYYY').startOf('day');
        return (!activatedDate || date.isSameOrAfter(activatedDate)) && (!expiredDate || date.isSameOrBefore(expiredDate));
      });
    },

    timeLimitedRateMessage() {
      const formatDate = date => date ? moment(date).format('DD MMM YYYY') : null;
      const formatActivatedDate = formatDate(this.activeRate.activated_at);
      const formatExpiredDate = formatDate(this.activeRate.expired_at);
      const message = (limits, limitsSuffix) => `This rate is only active ${limits}, you can enter time ${limitsSuffix}.`;

      if (formatActivatedDate && formatExpiredDate) {
        return message(`between ${formatActivatedDate} and ${formatExpiredDate}`, 'only within this timeframe');
      } else if (formatActivatedDate && !formatExpiredDate) {
        return message(`from ${formatActivatedDate}`, 'no earlier than this date');
      } else if (!formatActivatedDate && formatExpiredDate) {
        return message(`until ${formatExpiredDate}`, 'no later than this date');
      }
      return null;
    }
  },

  methods: {
    isNumeric(value) {
      return typeof value === 'number' && !isNaN(value);
    },

    getDayOfWeek(dateString) {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {weekday: 'short'});
    },

    resetTime() {
      this.times = this.times.map((time, index) => this.editableTime[index] ? time : 0);
    },
  },

}
</script>

<style scoped>
.dp__input {
  font-size: 0.75rem;
  padding-top: 4px;
  padding-bottom: 4px;
}

.select-wrapper {
  position: relative;
  display: inline-block;
  width: 100%;
}

.stub {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: transparent;
}
</style>
